import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { regler } from './pilotage'

/**
 * La chorégraphie de la page : ce qui entre, ce qui se révèle au défilement, et
 * les micro-interactions.
 *
 * ⭐ SÉPARÉ DE `MiseEnScene` PARCE QUE CE SONT DEUX MÉTIERS. Là-bas, le cycle de
 * vie : quand démarrer, quand s'arrêter, quels filets, quels écouteurs défaire.
 * Ici, la mise en scène elle-même. Les mélanger avait poussé le fichier
 * au-dessus des quatre cents lignes du dépôt, et un fichier qu'on relit en
 * diagonale est exactement l'endroit où un défaut s'installe sans être vu.
 */

/**
 * L'ouverture. Deux secondes, et elle se termine sur la page telle qu'elle doit
 * rester. Rend le numéro de son propre filet de sécurité.
 *
 * ⭐ L'ORDRE RACONTE QUELQUE CHOSE : le cadre du poinçon d'abord, vide ; l'objet
 * arrive dedans par ses propres moyens ; puis la mention d'autorité, le titre,
 * et le commanditaire. C'est l'ordre dans lequel on fabrique la pièce, pas
 * l'ordre du code.
 *
 * ⚠️ La gravure du socle fermait cette liste jusqu'au 2026-09-09. Elle a été
 * sortie d'ici : elle appartient à l'arrivée de la scène, pas à l'ouverture de
 * la page. Voir `creuser()` en bas de ce fichier.
 */
export function ouvrir(racine: HTMLElement): { filet: number; coupure: () => void } {
  racine.dataset['anime'] = 'attente'


  const tl = gsap.timeline({
    defaults: { ease: 'power3.out' },
    onComplete: () => {
      clearTimeout(filet)
      /* ⭐ `joue` et non pas rien : les blocs plus bas doivent RESTER cachés,
         c'est le défilement qui les révèle. Retirer l'attribut d'un coup les
         ferait tous apparaître en bas de page. */
      racine.dataset['anime'] = 'joue'
    },
  })

  tl.fromTo('#poincon', { opacity: 0, scale: 0.965 }, { opacity: 1, scale: 1, duration: 1.1 })
    .fromTo(
      '[data-ouvre]',
      { opacity: 0, y: 18 },
      { opacity: 1, y: 0, duration: 0.9, stagger: 0.075 },
      0.3,
    )
    .fromTo('[data-ouvre-titre]', { opacity: 0, y: 34 }, { opacity: 1, y: 0, duration: 1.15 }, 0.55)

  /*
   * ⛔⛔ LA GRAVURE N'EST PLUS ICI, ET C'EST LE CORRECTIF DU 2026-09-09. Elle se
   * creusait à t = 0,95 s de cette ligne de temps. Or le canvas est différé et
   * son morceau pèse : il se monte deux à cinq secondes plus tard. L'animation
   * se jouait donc AVANT que quoi que ce soit ne puisse la montrer, et l'objet
   * apparaissait déjà gravé.
   *
   * ⚠️ Elle se voyait quand même sur une machine assez rapide pour monter la
   * scène avant la fin de l'ouverture. D'où un défaut qui n'en avait pas l'air :
   * « ça se lance pas à chaque fois ». Ce n'était pas un aléa, c'était une
   * course entre deux horloges dont une seule était visible.
   *
   * ⭐ C'est `Poincon` qui appelle `creuser()`, à l'instant où la scène rend sa
   * première image utile. Le déclencheur est l'évènement visible, pas une
   * horloge partie avant lui.
   */

  /*
   * ⛔ LE FILET DE LA LIGNE DE TEMPS. Une fois les états de départ posés en
   * style en ligne, l'attribut CSS ne sauve plus rien : `opacity: 0` en ligne
   * bat n'importe quel sélecteur. Si la boucle d'images s'arrête ICI — page
   * masquée juste après le premier battement, onglet basculé, machine qui
   * décroche — la page reste vide sans qu'aucune erreur ne soit levée.
   * `progress(1)` rend l'état final immédiatement, sans dépendre d'une image.
   */
  const filet = window.setTimeout(() => {
    tl.progress(1)
    racine.dataset['anime'] = 'joue'
  }, 4000)

  /*
   * ⭐ L'OUVERTURE EST INTERRUPTIBLE, et c'est une question de respect. Le
   * premier clic, la première molette ou la première touche veut dire « j'ai
   * compris, montre-moi la page ». Faire attendre deux secondes de plus
   * quelqu'un qui a déjà décidé transforme une entrée en péage.
   * ⚠️ `progress(1)` pose l'état final immédiatement, il n'accélère pas la
   * suite : couper une animation doit donner la page, pas la même animation en
   * plus rapide.
   */
  const couper = () => {
    /* ⚠️ `progress(1)` NE DÉCLENCHE PAS `onComplete` : GSAP supprime les
       rappels quand on pose la position à la main. Les deux sorties anticipées,
       celle-ci et le filet de sécurité, doivent donc rendre la main
       elles-mêmes, sinon le style en ligne resterait posé et le cachet
       n'aurait plus le droit de se réduire. */
    tl.progress(1)
    racine.dataset['anime'] = 'joue'
    coupure()
  }
  const evenements = ['pointerdown', 'wheel', 'keydown', 'touchstart'] as const
  const coupure = () => {
    for (const e of evenements) window.removeEventListener(e, couper)
  }
  for (const e of evenements) window.addEventListener(e, couper, { passive: true, once: false })

  return { filet, coupure }
}

/**
 * Le récit au défilement.
 *
 * ⚠️ `batch` plutôt qu'un déclencheur par ligne : les onze articles entrent
 * ensemble dans l'écran, et onze déclencheurs indépendants produisent onze
 * décalages qui ne s'accordent sur rien. `batch` les regroupe par arrivée et
 * leur donne UN décalage commun, ce qui est exactement l'effet recherché.
 */
/*
 * ⚠️ PLUS DE PARAMÈTRE `signal`. Il était reçu et jamais lu : 🟢 relevé le
 * 2026-09-11 par ESLint (`no-unused-vars`). Vérifié avant de le retirer, et ce
 * n'était PAS une fuite : cette fonction ne pose aucun écouteur du document. Tout
 * ce qu'elle crée, tweens et déclencheurs, vit dans le `gsap.context` de
 * `demarrer` et meurt avec lui. ⛔ Le jour où elle pose un vrai `addEventListener`,
 * le signal doit revenir, sinon chaque remontage laisse un écouteur derrière lui.
 */
export function raconter() {
  for (const bloc of gsap.utils.toArray<HTMLElement>('[data-revele]')) {
    gsap.to(bloc, {
      opacity: 1,
      y: 0,
      duration: 0.85,
      ease: 'power3.out',
      scrollTrigger: { trigger: bloc, start: 'top 86%' },
    })
  }

  ScrollTrigger.batch('[data-articles] li', {
    start: 'top 92%',
    onEnter: (lot) =>
      gsap.to(lot, { opacity: 1, x: 0, duration: 0.6, stagger: 0.055, ease: 'power2.out' }),
  })

  /* Les filets sous les titres : une classe, une transition CSS. Le faire en
     JavaScript dupliquerait une règle qui existe déjà. */
  for (const trait of gsap.utils.toArray<HTMLElement>('[data-trace]')) {
    ScrollTrigger.create({
      trigger: trait,
      start: 'top 88%',
      once: true,
      onEnter: () => trait.classList.add('trace'),
    })
  }

  /* ⭐ L'objet réagit au défilement : la lumière tourne un peu pendant qu'on
     descend, donc la pièce n'est pas la même en haut et en bas de page. C'est
     la seule chose qui relie visuellement le poinçon au texte qui le suit. */
  ScrollTrigger.create({
    trigger: '.poincon',
    start: 'top top',
    end: 'bottom bottom',
    onUpdate: (self) => regler({ avancee: self.progress }),
  })

  /*
   * ⭐⭐ LE SCEAU QUI DEVIENT MARQUE. Passé le cadre, la pièce réapparaît en
   * petit dans le coin et y reste : elle cesse d'être le sujet pour devenir le
   * cachet apposé sur le document. C'est le SEUL effet lié au défilement de
   * toute la page, et c'est exactement pour ça qu'il se remarque.
   *
   * ⛔ CE N'EST PAS LE POINÇON QUI VOYAGE, c'est un second élément qui
   * apparaît. Le déplacer a été essayé deux fois et raté deux fois : en animant
   * sa taille, chaque image redimensionnait le canvas et R3F réallouait sa
   * cible de réfraction de 2048 pixels de côté, d'où deux à trois secondes de
   * passage ; en animant sa transformation, la ligne de temps d'ouverture
   * tenait déjà cette propriété en style en ligne et le cachet restait à taille
   * pleine. Une scène WebGL n'a rien à faire en voyage à travers une page.
   *
   * ⭐ Une seule classe sur la racine commande tout : le cachet qui monte, et
   * la réglette qui s'efface du même coin. Le CSS fait le reste, donc il n'y a
   * plus rien à mesurer ni à recalculer au défilement.
   */
  const racinePage = document.querySelector('.poincon')
  ScrollTrigger.create({
    trigger: '.poincon-cadre',
    start: 'bottom 100px',
    onEnter: () => racinePage?.classList.add('cachet-pose'),
    onLeaveBack: () => racinePage?.classList.remove('cachet-pose'),
  })

  /*
   * ⭐ La gravure se rejoue UNE SEULE FOIS, quand on remonte tout en haut après
   * avoir descendu. Jamais plus. Un effet qu'on peut déclencher en boucle perd
   * son statut d'évènement : il devient un jouet, et on cesse de le regarder.
   */
  let dejaRejouee = false
  ScrollTrigger.create({
    trigger: '.poincon-cadre',
    start: 'top 60%',
    onEnterBack: () => {
      if (dejaRejouee) return
      dejaRejouee = true
      creuser()
    },
  })
}

/**
 * Creuse la gravure du socle, de vide à pleine.
 *
 * ⭐ Exporté : `Poincon` l'appelle quand la scène rend sa première image utile,
 * et `raconter` le rejoue une fois si on remonte tout en haut. Deux appelants,
 * une seule écriture de l'effet.
 *
 * ⚠️ Sous `prefers-reduced-motion`, la gravure est POSÉE, pas animée. Le réglage
 * ne demande pas moins de contenu, il demande que rien ne bouge sans qu'on
 * l'ait décidé. ⛔ Et le contrôle est ici plutôt que chez les appelants : deux
 * appelants qui vérifient chacun de leur côté finissent par diverger, et c'est
 * celui du cas rare qui oublie.
 */
export function creuser() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    regler({ gravure: 1 })
    return
  }
  gsap.fromTo(
    { valeur: 0 },
    { valeur: 0 },
    {
      valeur: 1,
      duration: 1,
      ease: 'none',
      onUpdate() {
        regler({ gravure: (this['targets']()[0] as { valeur: number }).valeur })
      },
    },
  )
}

/**
 * Les micro-interactions.
 *
 * ⭐ LE NUMÉRO SE COMPOSE CARACTÈRE PAR CARACTÈRE, comme une frappe. ⚠️ Et la
 * largeur ne bouge pas d'un pixel : les caractères non encore posés sont
 * remplacés par des espaces, pas retirés. En monospace, la ligne garde donc
 * exactement sa place, sinon la réglette pousserait la mise en page à chaque
 * caractère.
 */
const ALPHABET = '0123456789ABCDEF'
/** Trois glyphes traversés par caractère avant qu'il ne se fixe. */
const PASSES = 3

export function composer() {
  for (const cible of gsap.utils.toArray<HTMLElement>('[data-compose]')) {
    const complet = cible.textContent ?? ''
    const pas = complet.length * (PASSES + 1)
    const avancee = { n: 0 }
    const rendre = () => {
      cible.textContent = complet
    }
    gsap.to(avancee, {
      n: pas,
      duration: 0.9,
      delay: 1.2,
      ease: 'steps(' + pas + ')',
      /* ⛔ Le texte n'est vidé qu'au DÉMARRAGE, jamais à la construction. GSAP
         restaure les styles au nettoyage, pas le contenu d'un élément : une
         animation posée puis annulée avant de démarrer laisserait la réglette
         vide pour toujours, sans erreur. Et toute sortie la remplit. */
      onStart: () => {
        cible.textContent = ' '.repeat(complet.length)
      },
      /*
       * ⭐ CHAQUE CARACTÈRE TRAVERSE TROIS GLYPHES AVANT DE SE FIXER. Sur un
       * numéro, c'est un cadran qui se cale ; la même chose sur une phrase
       * serait un cliché de terminal, et le fichier de direction l'interdit
       * explicitement pour le titre.
       * ⚠️ L'alphabet est hexadécimal et la police monospace : la largeur ne
       * bouge donc jamais d'un pixel, sinon la réglette pousserait la mise en
       * page à chaque caractère.
       */
      onUpdate: () => {
        const etape = Math.round(avancee.n)
        const fixes = Math.floor(etape / (PASSES + 1))
        const enCours = etape % (PASSES + 1)
        let sortie = complet.slice(0, fixes)
        if (fixes < complet.length) {
          sortie += enCours === 0 ? ' ' : ALPHABET[Math.floor(Math.random() * ALPHABET.length)]
          sortie += ' '.repeat(complet.length - fixes - 1)
        }
        cible.textContent = sortie
      },
      onComplete: rendre,
      onInterrupt: rendre,
    })
  }
}
